import React, { useState, useEffect, useCallback } from 'react';
import API from '../../../utils/api';
import { useApp } from '../../../context/AppContext';
import { Card, SectionHead, Btn, Badge, Table, Tr, Td, Modal, FormGroup, FormGrid, Input, CustomSelect, Textarea } from '../../common/UI';
import { generateFleetPDF, generateFleetJobSlipPDF } from '../../../utils/pdfGenerator';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const emptyJobForm = {
  job_date: new Date().toISOString().split('T')[0],
  customer_id: '', vessel_id: '', stock_entry_id: '',
  collection_location: '', collection_time: '',
  loading_time: '', truck_number: '', dropoff_location: '', cargo_description: '', dimensions: ''
};

const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

/* Friendly 12-hour Hour/Minute/AM-PM picker — stores value as 24hr "HH:MM" */
function TimePicker({ value, onChange }) {
  let hour = '09', minute = '00', period = 'AM';
  if (value) {
    const [h, m] = value.split(':').map(Number);
    period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    hour = String(h12).padStart(2, '0');
    minute = String(m).padStart(2, '0');
  }

  const emit = (h12, m, p) => {
    let h24 = parseInt(h12, 10) % 12;
    if (p === 'PM') h24 += 12;
    onChange(`${String(h24).padStart(2, '0')}:${m}`);
  };

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <CustomSelect value={hour} onChange={e => emit(e.target.value, minute, period)} style={{ flex: 1 }}>
        {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
      </CustomSelect>
      <CustomSelect value={minute} onChange={e => emit(hour, e.target.value, period)} style={{ flex: 1 }}>
        {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
      </CustomSelect>
      <CustomSelect value={period} onChange={e => emit(hour, minute, e.target.value)} style={{ width: 78 }}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </CustomSelect>
    </div>
  );
}

const wrapCell = { whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'visible', textOverflow: 'clip' };

function formatTime(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
}

export default function FleetManagement() {
  const { customers, vessels, trucks, activeStock, showToast, role } = useApp();
  const canSeeCommercials = role !== 'fleet_controller';
  const canEditJobDetails = role === 'fleet_controller' || role === 'admin';
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null); // null = creating a new job; otherwise editing this job id
  const [form, setForm] = useState(emptyJobForm);
  const [invoiceTarget, setInvoiceTarget] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({ invoice_number: '', invoice_value: '', remarks: '' });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const fetchJobs = useCallback(async () => {
    try {
      const { data } = await API.get('/fleet');
      setJobs(data);
    } catch (err) {
      showToast('Error loading fleet jobs', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyJobForm);
    setShowCreate(true);
  };

  const openEdit = (job) => {
    setEditId(job.id);
    setForm({
      job_date: job.job_date || new Date().toISOString().split('T')[0],
      customer_id: job.customer_id || '',
      vessel_id: job.vessel_id || '',
      stock_entry_id: job.stock_entry_id || '',
      collection_location: job.collection_location || '',
      collection_time: job.collection_time || '',
      loading_time: job.loading_time || '',
      truck_number: job.truck_number || '',
      dropoff_location: job.dropoff_location || '',
      cargo_description: job.cargo_description || '',
      dimensions: job.dimensions || ''
    });
    setShowCreate(true);
  };

  const handleSaveJob = async () => {
    if (!form.job_date || !form.truck_number || !form.dropoff_location) {
      showToast('Fill all required fields', 'error'); return;
    }
    try {
      if (editId) {
        await API.put(`/fleet/${editId}`, form);
        showToast('✓ Fleet job updated!', 'success');
      } else {
        await API.post('/fleet', form);
        showToast('✓ Fleet job created!', 'success');
      }
      setShowCreate(false);
      setEditId(null);
      setForm(emptyJobForm);
      fetchJobs();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error saving fleet job', 'error');
    }
  };

  const handleSaveInvoice = async () => {
    try {
      await API.put(`/fleet/${invoiceTarget.id}/invoice`, invoiceForm);
      showToast('✓ Invoice saved!', 'success');
      setInvoiceTarget(null);
      fetchJobs();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error saving invoice', 'error');
    }
  };

  const handleComplete = async (job) => {
    try {
      await API.put(`/fleet/${job.id}/complete`);
      showToast('✓ Job marked completed!', 'success');
      fetchJobs();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error completing job', 'error');
    }
  };

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(jobs.map(j => ({
      Fleet_Job_Number: j.fleet_job_number || '—',
      Ops_Job_Number: j.ops_job_number || '—',
      Date: j.job_date,
      Customer: j.Customer?.company_name || j.Customer?.company || '—',
      Vessel: j.Vessel?.name || '—',
      Linked_Stock_PO: j.StockEntry?.po_numbers || '—',
      Collection_Location: j.collection_location || '—',
      Collection_Time: j.collection_time ? formatTime(j.collection_time) : '—',
      Loading_Time: j.loading_time ? formatTime(j.loading_time) : '—',
      Truck_Number: j.truck_number || '—',
      Dropoff_Location: j.dropoff_location || '—',
      Dimensions: j.dimensions || '—',
      Cargo_Description: j.cargo_description || '—',
      Invoice_Number: j.invoice_number || '—',
      Invoice_Value: j.invoice_value || '—',
      Remarks: j.remarks || '—',
      Status: j.status
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fleet Management');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
    saveAs(blob, `AllIn_FleetManagement_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="fade-up page-root">
      <SectionHead title="Fleet Management" subtitle="Track truck loading jobs, invoices and completion">
        {canEditJobDetails && (
          <Btn variant="primary" onClick={openCreate}>
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Job
          </Btn>
        )}
      </SectionHead>

      <Card style={{ padding: 0 }}>
        <Table
          headers={[
            'Fleet Job #', 'Ops Job #', 'Date', 'Customer', 'Vessel', 'Linked Stock',
            'Collection Location', 'Collection Time', 'Loading Time', 'Truck #', 'Drop-off Location', 'Dimensions', 'Cargo Description',
            ...(canSeeCommercials ? ['Invoice #', 'Invoice Value', 'Remarks'] : []),
            'Status', 'Print', ''
          ]}
          empty={!loading && jobs.length === 0 ? 'No fleet jobs yet.' : null}>
          {jobs.map(job => (
            <Tr key={job.id}>
              <Td mono>{job.fleet_job_number || '—'}</Td>
              <Td mono>{job.ops_job_number || '—'}</Td>
              <Td>{job.job_date}</Td>
              <Td strong>{job.Customer?.company_name || job.Customer?.company || '—'}</Td>
              <Td>{job.Vessel?.name || '—'}</Td>
              <Td>{job.StockEntry?.po_numbers || '—'}</Td>
              <Td style={wrapCell}>{job.collection_location || '—'}</Td>
              <Td>{job.collection_time ? formatTime(job.collection_time) : '—'}</Td>
              <Td>{job.loading_time ? formatTime(job.loading_time) : '—'}</Td>
              <Td mono>{job.truck_number || '—'}</Td>
              <Td>{job.dropoff_location || '—'}</Td>
              <Td style={wrapCell}>{job.dimensions || '—'}</Td>
              <Td style={wrapCell}>{job.cargo_description || '—'}</Td>
              {canSeeCommercials && <Td>{job.invoice_number || '—'}</Td>}
              {canSeeCommercials && <Td>{job.invoice_value ? `$${job.invoice_value}` : '—'}</Td>}
              {canSeeCommercials && <Td style={wrapCell}>{job.remarks || '—'}</Td>}
              <Td><Badge status={job.status} /></Td>
              <Td center>
                <Btn size="xs" variant="outline" onClick={() => generateFleetJobSlipPDF(job)}>🖨 Print</Btn>
              </Td>
              <Td center>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                  {canEditJobDetails && job.status !== 'completed' && (
                    <Btn size="xs" variant="outline" onClick={() => openEdit(job)}>✎ Edit</Btn>
                  )}
                  {canSeeCommercials && job.status !== 'completed' && (
                    <>
                      <Btn size="xs" variant="outline" onClick={() => {
                        setInvoiceTarget(job);
                        setInvoiceForm({ invoice_number: job.invoice_number || '', invoice_value: job.invoice_value || '', remarks: job.remarks || '' });
                      }}>🧾 Invoice</Btn>
                      <Btn size="xs" variant="success" disabled={!job.invoice_number} onClick={() => handleComplete(job)}>✓ Complete</Btn>
                    </>
                  )}
                </div>
              </Td>
            </Tr>
          ))}
        </Table>
        <div style={{ display: 'flex', gap: 8, padding: '10px 16px', borderTop: '1px solid var(--border)' }}>
          <Btn variant="outline" size="sm" onClick={handleExportExcel}>📊 Export Excel</Btn>
          <Btn variant="primary" size="sm" onClick={() => generateFleetPDF(jobs)}>↓ Export PDF</Btn>
        </div>
      </Card>

      {/* Create / Edit Job Modal */}
      <Modal open={showCreate} onClose={() => { setShowCreate(false); setEditId(null); }}
        title={editId ? 'Edit Fleet Job' : 'Add New Fleet Job'} icon="🚛"
        footer={<>
          <Btn variant="outline" onClick={() => { setShowCreate(false); setEditId(null); }}>Cancel</Btn>
          <Btn variant="primary" onClick={handleSaveJob}>{editId ? 'Save Changes' : 'Create Job'}</Btn>
        </>}>
        <FormGrid>
          <FormGroup label="Job Date" required>
            <Input type="date" value={form.job_date} onChange={e => set('job_date', e.target.value)} />
          </FormGroup>
          <FormGroup label="Customer">
            <CustomSelect value={form.customer_id} onChange={e => set('customer_id', e.target.value)}>
              <option value="">— Select —</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.company_name || c.company}</option>)}
            </CustomSelect>
          </FormGroup>
          <FormGroup label="Vessel">
            <CustomSelect value={form.vessel_id} onChange={e => set('vessel_id', e.target.value)}>
              <option value="">— Select —</option>
              {vessels.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </CustomSelect>
          </FormGroup>
          <FormGroup label="Linked Stock Item">
            <CustomSelect value={form.stock_entry_id} onChange={e => set('stock_entry_id', e.target.value)}>
              <option value="">— None —</option>
              {activeStock.map(s => <option key={s.id} value={s.id}>{s.po} · {s.vessel}</option>)}
            </CustomSelect>
          </FormGroup>
          <FormGroup label="Collection Location">
            <Input value={form.collection_location} onChange={e => set('collection_location', e.target.value)} placeholder="Pickup location" />
          </FormGroup>
          <FormGroup label="Collection Time">
            <TimePicker value={form.collection_time} onChange={v => set('collection_time', v)} />
          </FormGroup>
          <FormGroup label="Loading Time">
            <TimePicker value={form.loading_time} onChange={v => set('loading_time', v)} />
          </FormGroup>
          <FormGroup label="Truck / Plate Number" required>
            <CustomSelect value={form.truck_number} onChange={e => set('truck_number', e.target.value)}>
              <option value="">— Select Truck —</option>
              {trucks.map(t => <option key={t.id} value={t.truck_number}>{t.truck_number}</option>)}
            </CustomSelect>
          </FormGroup>
          <FormGroup label="Drop-off Location" required full>
            <Input value={form.dropoff_location} onChange={e => set('dropoff_location', e.target.value)} />
          </FormGroup>
          <FormGroup label="Dimensions" hint="Flexible — enter in any format that fits this job" full>
            <Input value={form.dimensions} onChange={e => set('dimensions', e.target.value)} placeholder="e.g. 100x100x100 cm, or multiple items" />
          </FormGroup>
          <FormGroup label="Cargo Description" full>
            <Textarea value={form.cargo_description} onChange={e => set('cargo_description', e.target.value)} />
          </FormGroup>
        </FormGrid>
      </Modal>

      {/* Invoice Modal */}
      <Modal open={!!invoiceTarget} onClose={() => setInvoiceTarget(null)}
        title="Add Invoice" icon="🧾"
        footer={<>
          <Btn variant="outline" onClick={() => setInvoiceTarget(null)}>Cancel</Btn>
          <Btn variant="primary" onClick={handleSaveInvoice}>Save Invoice</Btn>
        </>}>
        <FormGrid>
          <FormGroup label="Invoice Number">
            <Input value={invoiceForm.invoice_number} onChange={e => setInvoiceForm(p => ({ ...p, invoice_number: e.target.value }))} />
          </FormGroup>
          <FormGroup label="Invoice Value (optional)">
            <Input type="number" value={invoiceForm.invoice_value} onChange={e => setInvoiceForm(p => ({ ...p, invoice_value: e.target.value }))} />
          </FormGroup>
          <FormGroup label="Remarks" full>
            <Textarea value={invoiceForm.remarks} onChange={e => setInvoiceForm(p => ({ ...p, remarks: e.target.value }))} />
          </FormGroup>
        </FormGrid>
      </Modal>
    </div>
  );
}
